import {
  afterRenderEffect,
  Component,
  computed,
  effect,
  type ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';

// import { SavedFiltersService } from '../../../services/filtering/saved-filters';
import { FilteringService } from '../../../services/filtering';
import { SaveService } from '../../../services/save/save';
import { StateService } from '../../../services/state/state';
import { Select } from '../../select/select';

@Component({
  imports: [Select],
  selector: 'app-filters-saved',
  styleUrl: './saved-filters.css',
  templateUrl: './saved-filters.html',
})
export class SavedFilters {
  protected readonly filteringService = inject(FilteringService);
  protected readonly saveService = inject(SaveService);

  private readonly _stateService = inject(StateService);

  private readonly _nameInput = viewChild<ElementRef<HTMLInputElement>>('nameInput');

  protected readonly activeId = signal('');
  protected readonly activePresetName = computed(() => {
    const _id = this.activeId();

    if (!_id) {
      return '';
    }

    return this.filteringService.userSavedSearches().find((f) => f.id === _id)?.label ?? '';
  });
  protected readonly isSaving = signal(false);
  protected readonly presetOptions = computed(() => {
    return [
      { label: 'Select a Saved Search', value: '' },
      ...this.filteringService.userSavedSearches().map((f) => ({ label: f.label, value: f.id })),
    ];
  });
  protected readonly saveName = signal('');

  private _confirmSaveTimeout: ReturnType<typeof setTimeout> | undefined = undefined;

  constructor() {
    afterRenderEffect(() => {
      if (this.isSaving()) {
        this._nameInput()?.nativeElement.focus();
        this._nameInput()?.nativeElement.select();
      }
    });

    effect(() => {
      const _defaultTitle = this.filteringService.userDefaultSavedSearchTitle();

      if (!_defaultTitle) {
        return;
      }

      if (!this.saveName()) {
        this.saveName.set(_defaultTitle);
      }
    });

    // Apply the saved search when the user picks one from the dropdown.
    // Uses activeId (not selectedSavedSearchId) so the dropdown display survives resetFilters().
    effect(() => {
      if (!this.activeId()) {
        return;
      }

      void this.filteringService.resetFilters();

      this.filteringService.selectedSavedSearchId.set(this.activeId());
      // untracked(() => this.filteringService.selectedSavedSearchId.set(this.activeId()));
    });
  }

  protected cancelSave(): void {
    this.isSaving.set(false);
    this.saveName.set('');
  }

  protected async confirmSave(): Promise<void> {
    if (this._confirmSaveTimeout) {
      return;
    }

    if (!this._stateService.userToken()) {
      this._stateService.handleAuthRequest();

      this._confirmSaveTimeout = setTimeout(() => {
        this._stateService.authPromiseRejecter?.();
        clearTimeout(this._confirmSaveTimeout);
        this._confirmSaveTimeout = undefined;
        return;
      }, 30000);
      // Wait an additional second to ensure the token is set.
      await this._stateService.authPromise();
    }

    if (!this._stateService.userToken()) {
      return;
    }

    clearTimeout(this._confirmSaveTimeout);
    this._confirmSaveTimeout = undefined;

    void this.saveService.saveSearch(this.saveName());
    // void this.savedFiltersService.save(this.saveName());
    this.isSaving.set(false);
    this.saveName.set('');
  }

  protected delete(id: string, event: Event): void {
    event.stopPropagation();
    // void this.savedFiltersService.delete(id);
  }

  protected async deleteActive(): Promise<void> {
    const _id = this.activeId();
    if (!_id) {
      return;
    }

    const _deleted = await this.saveService.deleteSavedItem(_id, 'listing-search');
    if (!_deleted) {
      return;
    }

    this.activeId.set('');
    void this.filteringService.resetFilters();
  }

  protected startSaving(): void {
    this.isSaving.set(true);
  }
}
